import React, { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { Progress } from "@/components/ui/progress"
import { supabase } from '@/integrations/supabase/client'
import { mailboxSyncService } from '@/lib/mailbox-sync-service'
import { toast } from 'sonner'
import { useClients } from '@/hooks/useClients'
import { useMailboxProviders } from '@/hooks/useMailboxProviders'
import { TimeTracker } from '@/lib/mailbox-sync/time-tracker'
import { logBulkOperation, updateBulkOperation } from '@/hooks/useBulkOperations'
import { ProviderManagerDialog } from './ProviderManagerDialog'
import { Settings } from 'lucide-react'

interface BulkEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedMailboxes: string[]
  selectedCount: number
  onUpdate: () => void
  onClearSelection: () => void
  providers: string[]
}

interface BulkUpdateData {
  // Core Smartlead settings
  message_per_day?: number
  min_time_btwn_emails?: number
  
  // Basic settings
  from_name?: string
  signature?: string
  status?: string
  provider?: string
  
  // Authentication
  app_password?: string
  email_password?: string
  username?: string
  
  // SMTP Settings
  smtp_host?: string
  smtp_port?: number
  
  // IMAP Settings
  imap_host?: string
  imap_port?: number
  is_different_imap_account?: boolean
  
  // Warmup settings
  warmup_status?: string
  warmup_reputation?: number
  warmup_sent_total?: number
  bounce_rate?: number
  spam_score?: number
  connection_health?: string
  
  // Advanced settings
  custom_tracking_domain?: string
  different_reply_to_address?: string
  
  // Cost and dates
  mailbox_cost?: string
  warmup_enabled_date?: string
  registration_date?: string
  
  // Profile and workspace admin
  profile_picture_url?: string
  is_workspace_admin?: boolean
}

// Mailbox status options that can be properly mapped to database constraints
const statusOptions = ['Active', 'Inactive', 'Deactivated', 'ORDERED/PURCHASED', 'WAITING FOR UPLOAD']

// Helper function to map UI status values to database values
const mapStatusToDatabase = (uiStatus: string): string => {
  switch (uiStatus) {
    case 'Active':
      return 'ACTIVE'
    case 'Inactive':
      return 'INACTIVE'
    case 'Deactivated':
      return 'DEACTIVATED'
    case 'ORDERED/PURCHASED':
      return 'ORDERED/PURCHASED'
    case 'WAITING FOR UPLOAD':
      return 'WAITING FOR UPLOAD'
    default:
      return 'ORDERED/PURCHASED' // Default fallback
  }
}

export function BulkEditDialog({
  open,
  onOpenChange,
  selectedMailboxes,
  selectedCount,
  onUpdate,
  onClearSelection,
  providers
}: BulkEditDialogProps) {
  const { data: clients = [] } = useClients()
  const { providers: mailboxProviders = [] } = useMailboxProviders()
  const [isLoading, setIsLoading] = useState(false)
  const [updateData, setUpdateData] = useState<BulkUpdateData>({})
  const [progress, setProgress] = useState({ current: 0, total: 0, timeRemaining: '' })
  const [showProviderManager, setShowProviderManager] = useState(false)

  const handleInputChange = (field: keyof BulkUpdateData, value: string | number | boolean) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleBulkUpdate = async () => {
    if (Object.keys(updateData).length === 0) {
      toast.error('Please select at least one field to update')
      return
    }

    setIsLoading(true)
    let operationId: string | null = null
    
    try {
      // Create operation details with "Previous → New" format
      // We'll update this with actual values as we process each mailbox
      let operationDetails: any = {}
      
      if (updateData.message_per_day !== undefined) {
        operationDetails.message_per_day = updateData.message_per_day
      }
      
      if (updateData.min_time_btwn_emails !== undefined) {
        operationDetails.min_time_btwn_emails = updateData.min_time_btwn_emails
      }

      // Log the operation start
      const operation = await logBulkOperation(
        'bulk_edit',
        selectedCount,
        operationDetails,
        'User'
      )
      operationId = operation.id
      
      // Close modal immediately and run in background
      onOpenChange(false)
      setUpdateData({})
      
      // Show initial toast
      const toastId = toast.loading(`Starting bulk update for ${selectedCount} mailboxes...`, {
        description: "This will run in the background to avoid rate limits",
        duration: Infinity
      })
      
      const timeTracker = new TimeTracker()
      let successful = 0
      let failed = 0
      const errors: string[] = []
      let aggregatedPreviousValues: any = {}
      let aggregatedNewValues: any = {}
      let processedMailboxes = 0
      
      // Process mailboxes sequentially to avoid rate limits (200/min = ~3 per second)
      for (let i = 0; i < selectedMailboxes.length; i++) {
        const mailboxId = selectedMailboxes[i]
        const current = i + 1
        let mailbox: any = null
        
        // Update toast with progress
        const timeRemaining = timeTracker.estimateTimeRemaining(current - 1, selectedCount)
        toast.loading(`Processing mailboxes: ${current}/${selectedCount}`, {
          id: toastId,
          description: `Time remaining: ${timeRemaining || 'Calculating...'} | Success: ${successful} | Failed: ${failed}`,
          duration: Infinity
        })
        
        try {
          // Get mailbox details first
          const { data: mailboxData, error: fetchError } = await supabase
            .from('mailboxes')
            .select('*')
            .eq('id', mailboxId)
            .single()

          if (fetchError || !mailboxData) {
            throw new Error(`Mailbox not found: ${fetchError?.message}`)
          }

          mailbox = mailboxData

          // Collect previous values for aggregation
          if (updateData.message_per_day !== undefined) {
            const previousValue = mailbox.message_per_day || 0
            if (!aggregatedPreviousValues.message_per_day) aggregatedPreviousValues.message_per_day = []
            if (!aggregatedNewValues.message_per_day) aggregatedNewValues.message_per_day = []
            aggregatedPreviousValues.message_per_day.push(previousValue)
            aggregatedNewValues.message_per_day.push(updateData.message_per_day)
          }
          
          if (updateData.min_time_btwn_emails !== undefined) {
            const previousValue = mailbox.daily_limit || 50
            if (!aggregatedPreviousValues.min_time_btwn_emails) aggregatedPreviousValues.min_time_btwn_emails = []
            if (!aggregatedNewValues.min_time_btwn_emails) aggregatedNewValues.min_time_btwn_emails = []
            aggregatedPreviousValues.min_time_btwn_emails.push(previousValue)
            aggregatedNewValues.min_time_btwn_emails.push(updateData.min_time_btwn_emails)
          }

          // Only call Smartlead API if we have Smartlead-specific fields to update
          const hasSmartleadUpdates = updateData.message_per_day !== undefined || updateData.min_time_btwn_emails !== undefined
          
          if (hasSmartleadUpdates) {
            if (!mailbox.smartlead_id) {
              throw new Error('Mailbox is not linked to Smartlead (required for sending settings)')
            }

            // Update Smartlead via API
            const smartleadApiKey = '19b11d82-9894-41db-8ca6-61988c208a29_80kb8ml'
            
            const smartleadPayload: any = {
              client_id: mailbox.client_id
            }
            
            if (updateData.message_per_day !== undefined) {
              smartleadPayload.max_email_per_day = updateData.message_per_day
            }
            
            if (updateData.min_time_btwn_emails !== undefined) {
              smartleadPayload.time_to_wait_in_mins = updateData.min_time_btwn_emails
            }

            console.log(`Updating mailbox ${mailbox.smartlead_id} with:`, smartleadPayload)

            const smartleadResponse = await fetch(
              `https://server.smartlead.ai/api/v1/email-accounts/${mailbox.smartlead_id}?api_key=${smartleadApiKey}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(smartleadPayload)
              }
            )

            if (!smartleadResponse.ok) {
              const errorText = await smartleadResponse.text()
              throw new Error(`Smartlead API error: ${smartleadResponse.status} - ${errorText}`)
            }
          }

          // Update local database with all fields
          const localUpdateData: any = {
            updated_at: new Date().toISOString()
          }
          
          // Add all the fields that can be updated
          if (updateData.message_per_day !== undefined) {
            localUpdateData.message_per_day = updateData.message_per_day
          }
          
          if (updateData.min_time_btwn_emails !== undefined) {
            localUpdateData.minimum_time_gap = updateData.min_time_btwn_emails
          }

          if (updateData.from_name !== undefined) {
            localUpdateData.from_name = updateData.from_name
          }

          if (updateData.signature !== undefined) {
            localUpdateData.signature = updateData.signature
          }

          if (updateData.status !== undefined) {
            localUpdateData.status = mapStatusToDatabase(updateData.status)
            // If status is being set to 'Deactivated', also update api_sync_status and deactivated_at
            if (updateData.status === 'Deactivated') {
              localUpdateData.api_sync_status = 'DEACTIVATED'
              localUpdateData.deactivated_at = new Date().toISOString()
              localUpdateData.warmup_status = 'INACTIVE'
            }
          }

          if (updateData.provider !== undefined) {
            localUpdateData.provider = updateData.provider
          }

          if (updateData.app_password !== undefined) {
            localUpdateData.app_password = updateData.app_password || null
          }

          if (updateData.email_password !== undefined) {
            localUpdateData.email_password = updateData.email_password || null
          }

          if (updateData.username !== undefined) {
            localUpdateData.username = updateData.username
          }

          if (updateData.smtp_host !== undefined) {
            localUpdateData.smtp_host = updateData.smtp_host
          }

          if (updateData.smtp_port !== undefined) {
            localUpdateData.smtp_port = updateData.smtp_port
          }

          if (updateData.imap_host !== undefined) {
            localUpdateData.imap_host = updateData.imap_host
          }

          if (updateData.imap_port !== undefined) {
            localUpdateData.imap_port = updateData.imap_port
          }

          if (updateData.is_different_imap_account !== undefined) {
            localUpdateData.is_different_imap_account = updateData.is_different_imap_account
          }

          if (updateData.custom_tracking_domain !== undefined) {
            localUpdateData.custom_tracking_domain = updateData.custom_tracking_domain
          }

          if (updateData.different_reply_to_address !== undefined) {
            localUpdateData.different_reply_to_address = updateData.different_reply_to_address
          }

          if (updateData.mailbox_cost !== undefined) {
            localUpdateData.mailbox_cost = updateData.mailbox_cost
          }

          if (updateData.warmup_enabled_date !== undefined) {
            localUpdateData.warmup_enabled_date = updateData.warmup_enabled_date || null
          }

          if (updateData.registration_date !== undefined) {
            localUpdateData.registration_date = updateData.registration_date || null
          }

          // Warmup settings
          if (updateData.warmup_status !== undefined) {
            localUpdateData.warmup_status = updateData.warmup_status
          }

          if (updateData.warmup_reputation !== undefined) {
            localUpdateData.warmup_reputation = updateData.warmup_reputation
          }

          if (updateData.warmup_sent_total !== undefined) {
            localUpdateData.warmup_sent_total = updateData.warmup_sent_total
          }

          if (updateData.bounce_rate !== undefined) {
            localUpdateData.bounce_rate = updateData.bounce_rate
          }

          if (updateData.spam_score !== undefined) {
            localUpdateData.spam_score = updateData.spam_score
          }

          if (updateData.connection_health !== undefined) {
            localUpdateData.connection_health = updateData.connection_health
          }

          const { error: updateError } = await supabase
            .from('mailboxes')
            .update(localUpdateData)
            .eq('id', mailboxId)

          if (updateError) {
            throw updateError
          }

          successful++
          processedMailboxes++
        } catch (error) {
          console.error(`Failed to update mailbox ${mailboxId}:`, error)
          failed++
          processedMailboxes++
          const mailboxEmail = mailbox?.email || mailboxId
          
          // Simplify error messages for better readability
          let simpleError = 'Unknown error'
          if (error instanceof Error) {
            const errorMessage = error.message.toLowerCase()
            if (errorMessage.includes('email account not found') || errorMessage.includes('account_not_found')) {
              simpleError = 'Mailbox not found in Smartlead'
            } else if (errorMessage.includes('not linked to smartlead') || !mailbox?.smartlead_id) {
              simpleError = 'Mailbox is not linked to Smartlead'
            } else if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests')) {
              simpleError = 'Rate limit exceeded, please try again later'
            } else if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
              simpleError = 'Permission denied'
            } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
              simpleError = 'Network connection error'
            } else {
              simpleError = 'Update failed'
            }
          }
          
          errors.push(`${mailboxEmail}: ${simpleError}`)
        }
        
        // Respect rate limits: 200 requests per minute = 1 request every 300ms
        if (i < selectedMailboxes.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 350))
        }
      }
      
      // Create final operation details with aggregated "Previous → New" format
      let finalOperationDetails: any = {}
      
      if (aggregatedPreviousValues.message_per_day && aggregatedNewValues.message_per_day) {
        const uniquePrevious = [...new Set(aggregatedPreviousValues.message_per_day)]
        const uniqueNew = [...new Set(aggregatedNewValues.message_per_day)]
        finalOperationDetails.message_per_day = `${uniquePrevious.join(', ')} → ${uniqueNew.join(', ')}`
      }
      
      if (aggregatedPreviousValues.min_time_btwn_emails && aggregatedNewValues.min_time_btwn_emails) {
        const uniquePrevious = [...new Set(aggregatedPreviousValues.min_time_btwn_emails)]
        const uniqueNew = [...new Set(aggregatedNewValues.min_time_btwn_emails)]
        finalOperationDetails.min_time_btwn_emails = `${uniquePrevious.join(', ')} → ${uniqueNew.join(', ')}`
      }

      // Update operation with final results and aggregated details
      if (operationId) {
        await updateBulkOperation(operationId, {
          successful_count: successful,
          failed_count: failed,
          errors: errors,
          operation_details: finalOperationDetails,
          completed_at: new Date().toISOString()
        })
      }

      // Final result toast
      toast.dismiss(toastId)
      if (failed === 0) {
        toast.success(`Successfully updated all ${selectedCount} mailboxes`, {
          description: `Completed in ${Math.round(timeTracker.getElapsedSeconds())}s`
        })
      } else {
        toast.warning(`Updated ${successful} mailboxes, ${failed} failed`, {
          description: `Completed in ${Math.round(timeTracker.getElapsedSeconds())}s`
        })
      }

      onUpdate()
      onClearSelection()
    } catch (error) {
      console.error('Bulk update failed:', error)
      
      // Update operation as failed
      if (operationId) {
        await updateBulkOperation(operationId, {
          successful_count: 0,
          failed_count: selectedCount,
          errors: [error instanceof Error ? error.message : 'Unknown error'],
        })
      }
      
      toast.error('Failed to update mailboxes')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedCount} mailboxes? This action cannot be undone.`)) {
      return
    }

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from('mailboxes')
        .delete()
        .in('id', selectedMailboxes)

      if (error) throw error

      toast.success(`Deleted ${selectedCount} mailboxes`)
      onUpdate()
      onClearSelection()
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to delete mailboxes')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBulkSync = async () => {
    setIsLoading(true)
    try {
      // Close modal immediately and run in background
      onOpenChange(false)
      
      const toastId = toast.loading(`Starting sync for ${selectedCount} mailboxes...`, {
        description: "This will run in the background to avoid rate limits",
        duration: Infinity
      })
      
      const timeTracker = new TimeTracker()
      let successful = 0
      let failed = 0
      
      // Process mailboxes sequentially to avoid rate limits (200/min = ~3 per second)
      for (let i = 0; i < selectedMailboxes.length; i++) {
        const id = selectedMailboxes[i]
        const current = i + 1
        
        // Update toast with progress
        const timeRemaining = timeTracker.estimateTimeRemaining(current - 1, selectedCount)
        toast.loading(`Syncing mailboxes: ${current}/${selectedCount}`, {
          id: toastId,
          description: `Time remaining: ${timeRemaining || 'Calculating...'} | Success: ${successful} | Failed: ${failed}`,
          duration: Infinity
        })
        
        try {
          await mailboxSyncService.updateMailboxInSmartlead(id, {})
          successful++
        } catch (error) {
          console.error(`Failed to sync mailbox ${id}:`, error)
          failed++
        }
        
        // Respect rate limits: 200 requests per minute = 1 request every 300ms
        if (i < selectedMailboxes.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 350))
        }
      }

      // Final result toast
      toast.dismiss(toastId)
      if (failed === 0) {
        toast.success(`Successfully synced all ${selectedCount} mailboxes`, {
          description: `Completed in ${Math.round(timeTracker.getElapsedSeconds())}s`
        })
      } else {
        toast.warning(`Synced ${successful} mailboxes, ${failed} failed`, {
          description: `Completed in ${Math.round(timeTracker.getElapsedSeconds())}s`
        })
      }
      
      onUpdate()
    } catch (error) {
      toast.error('Bulk sync failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Update {selectedCount} Mailboxes</DialogTitle>
          <DialogDescription>
            Update any mailbox settings for all selected mailboxes. Only fill in the fields you want to change. Blank fields will be ignored.
            <br />
            <span className="text-xs text-muted-foreground">
              Rate limit: 200 requests/minute. Large updates will run in background.
            </span>
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="basic">Basic Settings</TabsTrigger>
            <TabsTrigger value="sending">Email Sending</TabsTrigger>
            <TabsTrigger value="smtp">SMTP Settings</TabsTrigger>
            <TabsTrigger value="imap">IMAP Settings</TabsTrigger>
            <TabsTrigger value="warmup">Warmup Settings</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select onValueChange={(value) => handleInputChange('status', value)} value={updateData.status}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="provider">Mailbox Provider</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowProviderManager(true)}
                    className="h-6 px-2 text-xs"
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Manage
                  </Button>
                </div>
                <Select onValueChange={(value) => handleInputChange('provider', value)} value={updateData.provider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {mailboxProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.name}>
                        {provider.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="from_name">From Name</Label>
                <Input
                  id="from_name"
                  placeholder="Display name for emails"
                  value={updateData.from_name || ''}
                  onChange={(e) => handleInputChange('from_name', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mailbox_cost">Mailbox Cost</Label>
                <Input
                  id="mailbox_cost"
                  placeholder="$0.00/month"
                  value={updateData.mailbox_cost || ''}
                  onChange={(e) => handleInputChange('mailbox_cost', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="warmup_enabled_date">Warmup Enabled Date</Label>
                <Input
                  id="warmup_enabled_date"
                  type="date"
                  value={updateData.warmup_enabled_date || ''}
                  onChange={(e) => handleInputChange('warmup_enabled_date', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="registration_date">Registration Date</Label>
                <Input
                  id="registration_date"
                  type="date"
                  value={updateData.registration_date || ''}
                  onChange={(e) => handleInputChange('registration_date', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="signature">Email Signature</Label>
              <Textarea
                id="signature"
                placeholder="Plain text signature for outgoing emails"
                rows={4}
                value={updateData.signature || ''}
                onChange={(e) => handleInputChange('signature', e.target.value)}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Profile & Workspace Settings</h3>
              
              <div className="space-y-2">
                <Label htmlFor="profile_picture_url">Profile Picture URL</Label>
                <Input
                  id="profile_picture_url"
                  placeholder="https://example.com/photo.jpg"
                  value={updateData.profile_picture_url || ''}
                  onChange={(e) => handleInputChange('profile_picture_url', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  URL to the user's profile picture for these mailboxes
                </p>
              </div>

              <div className="flex items-center justify-between space-x-2 py-2 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="is_workspace_admin" className="text-sm font-medium cursor-pointer">
                    Google Workspace Admin
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Mark selected mailboxes as workspace administrators
                  </p>
                </div>
                <Switch
                  id="is_workspace_admin"
                  checked={updateData.is_workspace_admin || false}
                  onCheckedChange={(checked) => handleInputChange('is_workspace_admin', checked)}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sending" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="message_per_day">Message Per Day</Label>
                <Input
                  id="message_per_day"
                  type="number"
                  placeholder="e.g. 40"
                  min="0"
                  max="1000"
                  value={updateData.message_per_day || ''}
                  onChange={(e) => handleInputChange('message_per_day', parseInt(e.target.value) || undefined)}
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of emails to send per day for each mailbox
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="min_time_btwn_emails">Minimum Time Gap (minutes)</Label>
                <Input
                  id="min_time_btwn_emails"
                  type="number"
                  placeholder="e.g. 40"
                  min="1"
                  max="1440"
                  value={updateData.min_time_btwn_emails || ''}
                  onChange={(e) => handleInputChange('min_time_btwn_emails', parseInt(e.target.value) || undefined)}
                />
                <p className="text-xs text-muted-foreground">
                  Minimum time gap between emails in minutes for each mailbox
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Email Sending Settings:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Message Per Day and Time Gap are synced with Smartlead</li>
                <li>• All other fields are local database only</li>
                <li>• Changes apply immediately after update</li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="smtp" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="Email username"
                  value={updateData.username || ''}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP Host</Label>
                <Input
                  id="smtp_host"
                  placeholder="smtp.gmail.com"
                  value={updateData.smtp_host || ''}
                  onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="smtp_port">SMTP Port</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  placeholder="465"
                  value={updateData.smtp_port || ''}
                  onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value) || undefined)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email_password">Email Password</Label>
                <Input
                  id="email_password"
                  type="password"
                  placeholder="Email password"
                  value={updateData.email_password || ''}
                  onChange={(e) => handleInputChange('email_password', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app_password">App Password</Label>
              <Input
                id="app_password"
                type="password"
                placeholder="App-specific password for 2FA accounts"
                value={updateData.app_password || ''}
                onChange={(e) => handleInputChange('app_password', e.target.value)}
              />
            </div>
          </TabsContent>

          <TabsContent value="imap" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="imap_host">IMAP Host</Label>
                <Input
                  id="imap_host"
                  placeholder="imap.gmail.com"
                  value={updateData.imap_host || ''}
                  onChange={(e) => handleInputChange('imap_host', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="imap_port">IMAP Port</Label>
                <Input
                  id="imap_port"
                  type="number"
                  placeholder="993"
                  value={updateData.imap_port || ''}
                  onChange={(e) => handleInputChange('imap_port', parseInt(e.target.value) || undefined)}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_different_imap_account"
                checked={updateData.is_different_imap_account || false}
                onCheckedChange={(checked) => handleInputChange('is_different_imap_account', checked as boolean)}
              />
              <Label htmlFor="is_different_imap_account" className="text-sm font-medium">
                Use different email accounts for receiving emails
              </Label>
            </div>
          </TabsContent>

          <TabsContent value="warmup" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="warmup_status">Warmup Status</Label>
                <Select onValueChange={(value) => handleInputChange('warmup_status', value)} value={updateData.warmup_status}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select warmup status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="PAUSED">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="warmup_reputation">Warmup Reputation (%)</Label>
                <Input
                  id="warmup_reputation"
                  type="number"
                  placeholder="e.g. 85"
                  min="0"
                  max="100"
                  value={updateData.warmup_reputation || ''}
                  onChange={(e) => handleInputChange('warmup_reputation', parseInt(e.target.value) || undefined)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="warmup_sent_total">Total Warmup Emails Sent</Label>
                <Input
                  id="warmup_sent_total"
                  type="number"
                  placeholder="e.g. 150"
                  min="0"
                  value={updateData.warmup_sent_total || ''}
                  onChange={(e) => handleInputChange('warmup_sent_total', parseInt(e.target.value) || undefined)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bounce_rate">Bounce Rate (%)</Label>
                <Input
                  id="bounce_rate"
                  type="number"
                  placeholder="e.g. 2.5"
                  min="0"
                  max="100"
                  step="0.1"
                  value={updateData.bounce_rate || ''}
                  onChange={(e) => handleInputChange('bounce_rate', parseFloat(e.target.value) || undefined)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="spam_score">Spam Score</Label>
                <Input
                  id="spam_score"
                  type="number"
                  placeholder="e.g. 0.5"
                  min="0"
                  max="10"
                  step="0.1"
                  value={updateData.spam_score || ''}
                  onChange={(e) => handleInputChange('spam_score', parseFloat(e.target.value) || undefined)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="connection_health">Connection Health</Label>
                <Select onValueChange={(value) => handleInputChange('connection_health', value)} value={updateData.connection_health}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select connection health" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EXCELLENT">Excellent</SelectItem>
                    <SelectItem value="GOOD">Good</SelectItem>
                    <SelectItem value="FAIR">Fair</SelectItem>
                    <SelectItem value="POOR">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Warmup Settings:</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• These settings control warmup behavior and reputation tracking</li>
                <li>• Changes apply to local database only</li>
                <li>• Warmup status affects email sending capabilities</li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <Label htmlFor="custom_tracking_domain">Custom Tracking Domain</Label>
                <Input
                  id="custom_tracking_domain"
                  placeholder="tracking.yourdomain.com"
                  value={updateData.custom_tracking_domain || ''}
                  onChange={(e) => handleInputChange('custom_tracking_domain', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Custom domain for email tracking
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="different_reply_to_address">Different Reply-To Address</Label>
                <Input
                  id="different_reply_to_address"
                  type="email"
                  placeholder="replies@yourdomain.com"
                  value={updateData.different_reply_to_address || ''}
                  onChange={(e) => handleInputChange('different_reply_to_address', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Set a different email address for replies
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-900 mb-2">Advanced Settings:</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• These settings are for advanced users only</li>
                <li>• Incorrect values may cause email delivery issues</li>
                <li>• Test changes on a small subset first</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleBulkUpdate} disabled={isLoading}>
            {isLoading ? 'Updating...' : `Update ${selectedCount} Mailboxes`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    <ProviderManagerDialog
      open={showProviderManager}
      onOpenChange={setShowProviderManager}
    />
    </>
  )
}